import { useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { match } from 'ts-pattern'
import { useMemo } from 'use-memo-one'

import { NumericFormat } from '@blade/std-lib'
import { Button, Input, ResetIcon } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { selectedNetworkAtom } from '@root/entities/network'
import { NumberUtil } from '@root/shared'
import {
  FilterButton,
  FilterPopover,
  SwitchCurrencyIcon,
} from '@root/shared/ui-kit'

import type { TableFilter } from '@root/shared/ui-kit/data-table/atoms/table-filter'

export interface FilterTotalProps<ColumnId extends string> {
  baseCurrency: CurrencyModel | undefined
  filter: TableFilter<ColumnId> | undefined
  toggleCurrencyType?: () => void
  currencyType?: 'usd' | 'base'
  setColumnFilter: (filter: TableFilter<ColumnId>) => void
  clearColumnFilter: () => void
  columnId: ColumnId
}

export const FilterTotal = <ColumnId extends string = string>({
  baseCurrency,
  filter,
  currencyType,
  setColumnFilter,
  clearColumnFilter,
  toggleCurrencyType,
  columnId,
}: FilterTotalProps<ColumnId>) => {
  if (filter && filter.type !== 'numeric') {
    throw new Error('FilterTotal: filter must be a numeric filter')
  }

  const quickVariantValues = useMemo(() => {
    if (
      currencyType === 'base' &&
      baseCurrency &&
      CurrencyModel.isNativeOrWrapped(baseCurrency.network)(baseCurrency)
    ) {
      return [0.5, 1, 5]
    }

    // in most cases, base currency is usd stable coin
    return [100, 1000, 5000]
  }, [currencyType, baseCurrency])

  const quickVariants = useMemo(() => {
    return quickVariantValues.map((value) => {
      const formatted = NumericFormat.formatPreset(value, 'full')
      return {
        value,
        label: `> ${NumericFormat.formatPreset(value, 'currency-amount-short')}`,
        formatted,
      }
    })
  }, [quickVariantValues])

  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [rawValue, setRawValue] = useState(
    filter?.value ? NumericFormat.formatPreset(filter.value, 'full') : '',
  )

  const [network] = useAtom(selectedNetworkAtom)

  const value = useMemo(() => {
    return NumberUtil.parseBigNumber(rawValue)
  }, [rawValue])

  const handleApply = () => {
    if (!value) {
      return
    }
    const filter: TableFilter<ColumnId> = {
      column: columnId,
      operator: 'gt',
      value: value.toNumber(),
      type: 'numeric',
    }
    setColumnFilter(filter)
    setIsOpen(false)
  }

  const handleReset = () => {
    clearColumnFilter()
    setRawValue('')
    setIsOpen(false)
  }

  const currencyTitle = match(currencyType)
    .with('base', () => {
      if (baseCurrency) {
        return baseCurrency.ticker
      }
      const nativeToken = CurrencyModel.nativeOf(network)
      return nativeToken.ticker
    })
    .otherwise(() => 'USD')

  const hasFilter = filter !== undefined

  return (
    <FilterTotalRoot>
      <FilterPopover
        trigger={<FilterButton active={isOpen || hasFilter} />}
        title={t('trade.filters.total.header', { currency: currencyTitle })}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <FilterTotalContent>
          <FilterTotalSelectionRow>
            {quickVariants.map((variant) => (
              <OptionButton
                key={variant.label}
                radius="rounded"
                variant="secondary"
                size="xs"
                aria-pressed={rawValue === variant.formatted}
                onClick={() => setRawValue(variant.formatted)}
              >
                {variant.label}
              </OptionButton>
            ))}
          </FilterTotalSelectionRow>
          <Input
            numeric
            allowLeadingZeros={false}
            placeholder="0"
            size="m"
            startContent={t('common.amount')}
            endContent={
              currencyType === 'base' ? (
                <CurrencyIcon
                  currency={CurrencyModel.nativeOf(network)}
                  size={16}
                />
              ) : (
                '$'
              )
            }
            value={rawValue}
            onChange={(e) => setRawValue(e.target.value)}
            css={css`
              --input-height: 37px;
            `}
          />
          <FilterTotalFooter>
            <FilterResetButton
              variant="ghost"
              radius="rounded"
              size="xs"
              onClick={handleReset}
              disabled={!value || value.isZero()}
            >
              <ResetIcon />
              {t('trade.filters.common.reset')}
            </FilterResetButton>
            <Button
              variant="solid"
              color="primary"
              radius="rounded"
              size="xs"
              onClick={handleApply}
              disabled={!value || value.isZero()}
            >
              {t('trade.filters.common.apply')}
            </Button>
          </FilterTotalFooter>
        </FilterTotalContent>
      </FilterPopover>

      <ChangeCurrencyTrigger
        onClick={(e) => {
          e.stopPropagation()
          toggleCurrencyType?.()
        }}
      >
        {currencyTitle}
        {toggleCurrencyType && <SwitchCurrencyIcon size={16} />}
      </ChangeCurrencyTrigger>
    </FilterTotalRoot>
  )
}

const FilterTotalRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--text-11);

  cursor: pointer;

  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-12);
  }
`

const FilterTotalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
`

const FilterTotalSelectionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  height: 30px;
  align-items: center;
  gap: 2px;
`

const FilterTotalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ChangeCurrencyTrigger = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`

const FilterResetButton = styled(Button)`
  gap: 4px;
  padding-left: 0px;
  font-weight: 400;
  &:hover {
    background: transparent;
  }
`

const OptionButton = styled(Button)`
  color: var(--text-11);
  --button-radius-rounded: 10px;
  &:hover {
    background: var(--background-3);
  }
  &[aria-pressed='true'] {
    background: var(--background-3);
  }
`
